import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import moment from "moment";

import { IDog, IObedienceProgress, IDogRunSummary, ISportProgress } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import { Modal, useModal } from "../../../components/Modal/Modal";
import ManualRunEntryModal from "../ManualRunEntry/ManualRunEntryModal";
import ContextMenu, { IContextMenuOption } from "../../../components/ContextMenu/ContextMenu";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";

import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";

import "./ObedienceCard.scss";

interface IProps {
  dog: IDog;
  obedienceData: IObedienceProgress;
  onRunUpdated?: () => void;
}

const ObedienceCard: React.FC<IProps> = ({ dog, obedienceData, onRunUpdated }) => {
  const runHistoryModal = useModal();
  const manualRunModal = useModal();
  const deleteConfirmModal = useModal();
  const [editingRun, setEditingRun] = useState<IDogRunSummary | null>(null);
  const [runToDelete, setRunToDelete] = useState<string | null>(null);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [localRuns, setLocalRuns] = useState<IDogRunSummary[]>(obedienceData.recent_runs || []);
  
  // Track if we need to refresh parent when modal closes
  const needsParentRefreshRef = useRef(false);
  
  // Sync localRuns with obedienceData when it changes
  useEffect(() => {
    setLocalRuns(obedienceData.recent_runs || []);
  }, [obedienceData.recent_runs]);

  const handleAddRun = () => {
    setEditingRun(null);
    manualRunModal.toggle();
  };

  const handleEditRun = (run: IDogRunSummary) => {
    setEditingRun(run);
    manualRunModal.toggle();
  };

  const handleDeleteRun = (runId: string) => {
    setRunToDelete(runId);
    deleteConfirmModal.toggle();
  };

  // Stable refs for AG Grid cell renderer callbacks (avoids stale closure issues)
  const handleEditRunRef = useRef(handleEditRun);
  const handleDeleteRunRef = useRef(handleDeleteRun);
  const deletingRunIdRef = useRef(deletingRunId);
  useEffect(() => { handleEditRunRef.current = handleEditRun; });
  useEffect(() => { handleDeleteRunRef.current = handleDeleteRun; });
  useEffect(() => { deletingRunIdRef.current = deletingRunId; }, [deletingRunId]);

  const confirmDelete = async () => {
    if (!runToDelete) return;

    setDeletingRunId(runToDelete);
    deleteConfirmModal.toggle();
    
    try {
      await apiService.deleteManualRun(runToDelete);
      toast.show({
        title: "Run Deleted",
        content: "The run has been removed and title progress has been recalculated.",
        duration: 5000,
        type: "success"
      });
      
      // Refresh the local runs list
      const response = await apiService.getRibbonRoomData(dog._id);
      const obedienceSport = response.sports.find((s: ISportProgress) => s.show_type === 'Obedience' && s.sanctioning_club === 'AKC');
      if (obedienceSport) {
        setLocalRuns(obedienceSport.recent_runs || []);
      }
      
      // Mark that parent needs refresh when modal closes
      needsParentRefreshRef.current = true;
    } catch (error: any) {
      console.error('Error deleting run:', error);
      toast.show({
        title: "Delete Failed",
        content: "Unable to delete the run. Please try again.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    } finally {
      setDeletingRunId(null);
      setRunToDelete(null);
    }
  };

  const handleRunSaved = async () => {
    manualRunModal.toggle();
    
    // Fetch fresh run data to update the table without closing the modal
    try {
      const response = await apiService.getRibbonRoomData(dog._id);
      const obedienceSport = response.sports.find((s: ISportProgress) => s.show_type === 'Obedience' && s.sanctioning_club === 'AKC');
      if (obedienceSport) {
        setLocalRuns(obedienceSport.recent_runs || []);
      }
    } catch (error) {
      console.error('Failed to refresh runs:', error);
    }
    
    // Mark that we need to refresh parent when the run history modal closes
    needsParentRefreshRef.current = true;
  };

  const handleCloseRunsModal = () => {
    runHistoryModal.toggle();
    
    // If we added/edited/deleted runs, refresh the parent to update title progress
    if (needsParentRefreshRef.current) {
      needsParentRefreshRef.current = false;
      onRunUpdated?.();
    }
  };

  const getActionMenuOptions = (run: IDogRunSummary) => {
    const isManualEntry = run.data_source === Constants.run_data_source.manual_entry;
    if (run.verified) return null;
    if (deletingRunIdRef.current === run._id) return null;
    if (!isManualEntry) return null;
    return [
      { label: 'Edit', onClick: () => handleEditRunRef.current(run) },
      { label: 'Delete', onClick: () => handleDeleteRunRef.current(run._id) }
    ] as IContextMenuOption[];
  };

  const ActionsCellRenderer = (params: any) => {
    const run = params.data;
    if (!run) return null;
    if (run.verified) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#10b981', fontWeight: 600 }}>Verified</span>;
    if (deletingRunIdRef.current === run._id) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Deleting...</span>;
    const options = params.getOptions(run);
    if (!options) return null;
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><ContextMenu options={options} disablePortal /></div>;
  };

  // AGGrid column definitions
  const columnDefs: ColDef[] = [
    {
      headerName: 'Date',
      field: 'trial_date',
      valueFormatter: (params) => params.value ? moment(params.value).format('M/D/YYYY') : '-',
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Class',
      field: 'class',
      valueFormatter: (params) => params.value || '-',
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Score',
      field: 'score',
      valueFormatter: (params) => params.value !== undefined ? params.value : '-',
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Result',
      field: 'result_status',
      valueFormatter: (params) => params.value || '-',
      cellStyle: (params) => {
        if (params.value === Constants.run_result_status.qualified) {
          return { color: '#10b981', fontWeight: 600 };
        } else if (params.value === Constants.run_result_status.not_qualified) {
          return { color: '#ef4444', fontWeight: 600 };
        }
        return undefined;
      },
      minWidth: 120,
      flex: 1
    },
    {
      headerName: 'Judge #',
      field: 'judge_number',
      valueFormatter: (params) => params.value || '-',
      minWidth: 100,
      flex: 1
    },
    {
      headerName: 'Judge Name',
      field: 'judge_name',
      valueFormatter: (params) => params.value || '-',
      minWidth: 150,
      flex: 1
    },
    {
      headerName: 'Event',
      field: 'event_name',
      valueFormatter: (params) => params.value || '-',
      minWidth: 200,
      flex: 2
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: { getOptions: getActionMenuOptions },
      cellStyle: { overflow: 'visible', zIndex: 1 },
      minWidth: 100,
      flex: 1
    }
  ];

  // Sort levels in correct order (Novice -> Open -> Utility)
  const levelOrder = [
    Constants.dog_class_element_level.obedience_novice,
    Constants.dog_class_element_level.obedience_open,
    Constants.dog_class_element_level.obedience_utility
  ];

  const sortedLevels = obedienceData.levels.sort((a, b) => {
    return levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level);
  });

  return (
    <div className="obedienceCard sportCard">
      {/* Header */}
      <div className="cardHeader">
        <div className="headerLeft">
          <h3>AKC Obedience</h3>
        </div>
        <div className="headerRight">
          <div className="statsGrid">
            <div className="statItem">
              <div className="statLabel">Total Titles</div>
              <div className="statValue">{obedienceData.earned_titles.length}</div>
            </div>
            <div className="statItem">
              <div className="statLabel">Qualified Runs</div>
              <div className="statValue">{obedienceData.total_qualified}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Earned Titles */}
      {obedienceData.earned_titles && obedienceData.earned_titles.length > 0 && (
        <div className="earnedTitlesSection">
          <div className="titleBadges">
            {obedienceData.earned_titles.map(title => (
              <div 
                key={title.title_code} 
                className="titleBadge"
                style={{ backgroundColor: title.color_hex || '#4CAF50' }}
              >
                <div className="badgeInfo">
                  <div className="badgeTitle">{title.title_code}</div>
                  <div className="badgeDate">{title.earned_date ? new Date(title.earned_date).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress Section */}
      <div className="progressSection">
        <div className="progressVertical">
          {/* CD, CDX, UD */}
          {sortedLevels.map((levelData, index) => {
            const isEarned = levelData.title_earned;
            const titleCode = levelData.title_code;
            const levelName = levelData.level;
            
            // Extract just the class name from the full level string (e.g., "Obedience Novice" -> "Novice")
            const shortLevelName = levelName.replace('Obedience ', '');

            return (
              <div key={levelData.level} className="levelRow">
                <div className="levelInfo">
                  <div className={classNames("levelTitle", { earned: isEarned })}>
                    {titleCode ? (
                      <>
                        <strong>{titleCode}</strong> ({shortLevelName})
                      </>
                    ) : (
                      levelName
                    )}
                    {isEarned && <span className="earnedCheck"> ✓</span>}
                  </div>
                </div>
                
                <div className="levelProgress">
                  <div className="progressText">{levelData.qualified_count}/{levelData.required_legs} Legs</div>
                  {levelData.highest_score && (
                    <div className="bestScore">Best: {levelData.highest_score}</div>
                  )}
                </div>

                {index < sortedLevels.length - 1 && (
                  <div className="levelConnector">↓</div>
                )}
              </div>
            );
          })}

          {/* UDX Section (if applicable) */}
          {obedienceData.udx && (
            <>
              <div className="levelConnector">↓</div>
              <div className="levelRow udxRow">
                <div className="levelInfo">
                  <div className={classNames("levelTitle", { earned: obedienceData.udx.title_earned })}>
                    UDX
                  </div>
                  <div className="levelName">Open B + Utility B</div>
                </div>
                
                <div className="levelProgress">
                  <div className="progressText">{obedienceData.udx.dual_qs}/{obedienceData.udx.required_dual_qs} Dual Qs</div>
                </div>

                {obedienceData.udx.title_earned && (
                  <div className="earnedBadge">✓</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="cardDivider"></div>

      {/* View Run History Link - Always show, even with 0 runs */}
      {onRunUpdated ? (
        <div className="viewRunsSection">
          <button onClick={runHistoryModal.toggle} className="viewRunsLink">
            View & Edit Run History ({obedienceData.recent_runs?.length || 0} runs)
          </button>
        </div>
      ) : obedienceData.recent_runs && obedienceData.recent_runs.length > 0 ? (
        <div className="viewRunsSection">
          <button onClick={runHistoryModal.toggle} className="viewRunsLink">
            View Run History ({obedienceData.recent_runs.length} runs)
          </button>
        </div>
      ) : null}

      {/* Run History Modal */}
      {runHistoryModal.isShown && (
        <Modal
          isShown={runHistoryModal.isShown}
          hide={handleCloseRunsModal}
          className="runsHistoryModal"
          modalContent={
            <div className="runsModal">
              <h2>AKC Obedience - Run History</h2>

              <div className="runsModalContent">
                <div className="runsSection">
                  <div className="runsSectionHeader">
                    <h4>All Runs</h4>
                  </div>
                  {obedienceData.recent_runs.length === 0 ? (
                    <div className="noRuns">
                      <p>No runs recorded yet.</p>
                    </div>
                  ) : (
                    <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
                      <AgGridReact
                        rowData={localRuns}
                        columnDefs={columnDefs}
                        domLayout='normal'
                        suppressCellFocus={true}
                        theme={themeQuartz}
                        getRowId={(params) => params.data._id}
                      />
                    </div>
                  )}
                </div>

                <div className="modalActions">
                  <Button onClick={handleAddRun}>
                    + Add Manual Run
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      )}

      {/* Manual Run Entry Modal */}
      {manualRunModal.isShown && (
        <ManualRunEntryModal
          isOpen={manualRunModal.isShown}
          onClose={manualRunModal.toggle}
          dog={dog}
          sportType={Constants.show_type.obedience}
          sanctioningClub={Constants.sanctioning_club.AKC}
          onSuccess={handleRunSaved}
          editRun={editingRun as any}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.isShown && (
        <Modal
          isShown={deleteConfirmModal.isShown}
          hide={deleteConfirmModal.toggle}
          className="deleteConfirmModal"
          modalContent={
            <div className="deleteConfirmModal">
              <h3>Delete Run?</h3>
              <p>Are you sure you want to delete this run? This action cannot be undone and will recalculate title progress.</p>
              <div className="modalActions">
                <Button variant="secondary" onClick={deleteConfirmModal.toggle}>
                  Cancel
                </Button>
                <Button variant="default" onClick={confirmDelete}>
                  Delete Run
                </Button>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default ObedienceCard;

